import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, useCan, useSelectedDealer } from "components";
import { createFormHelper } from "lib/Form";
import { SMS_GATEWAYS } from "models";
import { ASTERISK_PLACEHOLDER } from "modules/DealerOverviewDashboard/components";
import {
  useBuyTwilioNumberMutation,
  useCreateTwilioSubAccountMutation,
  useDeleteTwilioSubAccountMutation,
  useGenerateTwilioApiKeyMutation,
  useSetMonthlyDollarLimitMutation,
  useSetMonthlySmsLimitMutation,
  useTwilioNumberPriceQuery
} from "modules/DealerOverviewDashboard/components/Service/components/Twilio/hooks";
import "modules/DealerOverviewDashboard/components/Service/components/Twilio/Twilio.scss";
import { formatPrice } from "utils/formatPrice";

import type { DealerOverviewFormPayload } from "modules/DealerOverviewDashboard/hooks";

const getPasswordOrTextInputType = (hasValue?: boolean) => (hasValue ? "password" : "text");
const FormHelper = createFormHelper<DealerOverviewFormPayload>();

export const Twilio = () => {
  const { t } = useTranslation();
  const { watch, formState } = FormHelper.useFormContext();

  const { selectedDealer } = useSelectedDealer();

  const [confirmBuyPhoneNumber, setConfirmBuyPhoneNumber] = useState(false);
  const [confirmDeleteSubAccount, setConfirmDeleteSubAccount] = useState(false);

  const { mutate: setSmsLimit, isPending: isSetSmsLimitPending } = useSetMonthlySmsLimitMutation();
  const { mutate: setDollarLimit, isPending: isSetDollarLimitPending } = useSetMonthlyDollarLimitMutation();
  const { mutate: buyNumber, isPending: isBuyNumberPending } = useBuyTwilioNumberMutation();
  const { mutate: generateApiKey, isPending: isGenerateApiKeyPending } = useGenerateTwilioApiKeyMutation();
  const { mutate: createSubAccount, isPending: isCreateSubAccountPending } = useCreateTwilioSubAccountMutation();
  const { mutate: deleteSubAccount, isPending: isDeleteSubAccountPending } = useDeleteTwilioSubAccountMutation();
  const { data: twilioNumberPrice, isFetching: isTwilioNumberPriceFetching } = useTwilioNumberPriceQuery(confirmBuyPhoneNumber, selectedDealer?.id);

  const canSetSmsLimit = useCan("create", "twilio-sub-account-sms-limit");
  const canSetDollarLimit = useCan("create", "twilio-sub-account-dollar-limit");

  if (!selectedDealer) return null;

  const isManaged = selectedDealer.is_twilio_sub_account_managed;

  const [twilioMonthlySmsLimit, twilioMonthlyDollarLimit, twilioAccountSid, twilioApiKey, twilioPhoneNumber, smsGatewayId] = watch([
    "twilio_monthly_sms_limit",
    "twilio_monthly_dollar_limit",
    "twilio_account_sid",
    "twilio_api_key",
    "twilio_phone_number",
    "sms_gateway_id"
  ]);
  const hasAccountSid = Boolean(twilioAccountSid);
  const isTwilioSmsGatewayEnabled = smsGatewayId === SMS_GATEWAYS.Twilio;

  const handleBuyPhoneNumber = () => {
    buyNumber({ dealer_id: selectedDealer.id });
    setConfirmBuyPhoneNumber(false);
  };

  const handleDeleteSubAccount = () => {
    deleteSubAccount({ dealer_id: selectedDealer.id });
    setConfirmDeleteSubAccount(false);
  };

  const getBuyPhoneNumberConfirmMsg = () => {
    if (isTwilioNumberPriceFetching) return `${t("v8_retrieving_the_current_price", "Retrieving the current price")}...`;
    if (!twilioNumberPrice) return t("v8_could_not_retrieve_the_current_price", "Could not retrieve the current price. Please close this and try again.");

    return t("v8_buy_phone_number_confirm_message", "This will cost ${{price}} per month. Do you want to proceed?", { price: formatPrice(twilioNumberPrice) });
  };

  return (
    <>
      <div className="dealer-overview-subheading">{t("v8_twilio_settings", "Twilio Settings")}</div>
      <Form.Group className="twilio-mt">
        <Form.Field width={8} error={formState.isSubmitted && isTwilioSmsGatewayEnabled && !hasAccountSid}>
          <label>{t("v8_twilio_account_sid", "Twilio account SID")}</label>
          <FormHelper.Field.Input name="twilio_account_sid" inputProps={{ disabled: isManaged }} />
        </Form.Field>
        <Form.Field width={8}>
          <label>{t("v8_twilio_auth_token", "Twilio auth token")}</label>
          <FormHelper.Field.Input
            name="twilio_auth_token"
            preventAutofill
            inputProps={{
              type: getPasswordOrTextInputType(selectedDealer.has_twilio_auth_token),
              disabled: isManaged,
              placeholder: selectedDealer.has_twilio_auth_token ? ASTERISK_PLACEHOLDER : ""
            }}
          />
        </Form.Field>
      </Form.Group>

      {isManaged ? (
        <Can I="delete" the="twilio-sub-account">
          <div className="twilio-action">
            <Button color="red" type="button" onClick={() => setConfirmDeleteSubAccount(true)}>
              {t("v8_delete_sub_account", "Delete sub-account")}
            </Button>
          </div>
        </Can>
      ) : (
        <Can I="create" the="twilio-sub-account">
          <div className="twilio-action">
            <Button
              color="green"
              type="button"
              disabled={!!selectedDealer.twilio_account_sid || isCreateSubAccountPending}
              loading={isCreateSubAccountPending}
              onClick={() => createSubAccount({ dealer_id: selectedDealer.id })}
            >
              {t("v8_create_sub_account", "Create sub-account")}
            </Button>
          </div>
        </Can>
      )}
      <Form.Group className="twilio-mt">
        <Form.Field width={8} error={formState.isSubmitted && isTwilioSmsGatewayEnabled && !twilioApiKey}>
          <label>{t("v8_twilio_api_key", "Twilio API key SID")}</label>
          <FormHelper.Field.Input name="twilio_api_key" inputProps={{ disabled: isManaged }} />
        </Form.Field>
        <Form.Field width={8}>
          <label>{t("v8_twilio_api_secret", "Twilio API secret")}</label>
          <FormHelper.Field.Input
            name="twilio_api_secret"
            preventAutofill
            inputProps={{ type: getPasswordOrTextInputType(selectedDealer.has_twilio_api_secret), disabled: isManaged }}
          />
        </Form.Field>
      </Form.Group>
      <Can I="create" the="twilio-sub-account-api-key">
        <div className="twilio-action">
          <Button
            type="button"
            color="green"
            disabled={!!selectedDealer.twilio_api_key || isGenerateApiKeyPending}
            loading={isGenerateApiKeyPending}
            onClick={() => generateApiKey({ dealer_id: selectedDealer.id })}
          >
            {t("v8_create_api_key", "Create API Key")}
          </Button>
        </div>
      </Can>
      <Form.Group className="twilio-mt">
        <Form.Field width={8} error={formState.isSubmitted && isTwilioSmsGatewayEnabled && !twilioPhoneNumber}>
          <label>{t("v8_twilio_phone_number", "Twilio phone number")}</label>
          <FormHelper.Field.Input name="twilio_phone_number" />
        </Form.Field>
      </Form.Group>
      <Can I="create" the="twilio-sub-account-number">
        <Form.Field width={8}>
          <div className="twilio-action twilio-action-spaced">
            <Button
              type="button"
              color="green"
              disabled={isBuyNumberPending || !!selectedDealer.twilio_phone_number}
              loading={isBuyNumberPending}
              onClick={() => setConfirmBuyPhoneNumber(true)}
            >
              {t("v8_buy_phone_number", "Buy phone number")}
            </Button>
          </div>
        </Form.Field>
      </Can>

      {(canSetDollarLimit || canSetSmsLimit) && (
        <Form.Group>
          {canSetDollarLimit && (
            <Form.Field width={8}>
              <label>{t("v8_monthly_dollar_limit", "Monthly dollar limit")}</label>
              <FormHelper.Field.Input name="twilio_monthly_dollar_limit" inputProps={{ type: "number", disabled: !hasAccountSid }} />
              <div className="twilio-action">
                <Button
                  type="button"
                  className="mt-12 twilio-mt"
                  color="green"
                  disabled={!hasAccountSid || isSetDollarLimitPending}
                  loading={isSetDollarLimitPending}
                  onClick={() => setDollarLimit({ monthly_price: twilioMonthlyDollarLimit, dealer_id: selectedDealer.id })}
                >
                  {t("v8_set_monthly_dollar_limit", "Set monthly dollar limit")}
                </Button>
              </div>
            </Form.Field>
          )}
          {canSetSmsLimit && (
            <Form.Field width={8}>
              <label>{t("v8_monthly_sms_limit", "Monthly SMS limit")}</label>
              <FormHelper.Field.Input name="twilio_monthly_sms_limit" inputProps={{ type: "number", disabled: !hasAccountSid }} />
              <div className="twilio-action">
                <Button
                  type="button"
                  className="mt-12 twilio-mt"
                  color="green"
                  disabled={!hasAccountSid || isSetSmsLimitPending}
                  loading={isSetSmsLimitPending}
                  onClick={() => setSmsLimit({ monthly_sms: twilioMonthlySmsLimit, dealer_id: selectedDealer.id })}
                >
                  {t("v8_set_monthly_sms_limit", "Set monthly SMS limit")}
                </Button>
              </div>
            </Form.Field>
          )}
        </Form.Group>
      )}
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={confirmDeleteSubAccount}
        handleCancel={() => setConfirmDeleteSubAccount(false)}
        customTitle={t("v8_are_you_sure", "Are you sure?")}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        isLoading={isDeleteSubAccountPending}
        handleConfirm={handleDeleteSubAccount}
      />
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Warning}
        isOpen={confirmBuyPhoneNumber}
        handleCancel={() => setConfirmBuyPhoneNumber(false)}
        customTitle={t("v8_are_you_sure", "Are you sure?")}
        confirmMsg={getBuyPhoneNumberConfirmMsg()}
        isDisabled={!twilioNumberPrice}
        isLoading={isBuyNumberPending || isTwilioNumberPriceFetching}
        handleConfirm={handleBuyPhoneNumber}
      />
    </>
  );
};
